// helpers.py
export const wrapTextByChars = (text = "", max = 20) => {
    // Split words and build lines without exceeding max chars per line
    const words = String(text).split(/\s+/);
    const lines = [];
    let current = "";
    words.forEach((w, idx) => {
        // If a single word is longer than max, hard-split it
        if (w.length > max) {
            // push what we had so far
            if (current) {
                lines.push(current);
                current = "";
            }
            for (let i = 0; i < w.length; i += max) {
                lines.push(w.slice(i, i + max));
            }
            return;
        }
        const tentative = current ? `${current} ${w}` : w;
        if (tentative.length <= max) {
            current = tentative;
        } else {
            if (current) lines.push(current);
            current = w;
        }
        if (idx === words.length - 1 && current) lines.push(current);
    });
    if (!words.length) return [text];
    return lines;
};

export const marginFab = 12;
export const separationFab = 65;

export const LS_GOOGLE_ACCESS = "google_provider_token";
export const LS_GOOGLE_REFRESH = "google_provider_refresh_token";

export const zIndexBotones = 1000;
export const zIndexSearchBar = 1100;
export const zIndexModals = 1200;
export const canvasSize = 1500; // px
export const canvasCenter = 0; //canvasSize / 2;
export const AVATAR_SIZE = 40; // px

// ----------
const NODE_SIZE_HINT = { w: AVATAR_SIZE, h: AVATAR_SIZE };
const NODE_GAP = AVATAR_SIZE * 2;

// comment: main group selection per persona (using grupo_principal when available)
function computeMainGroupByPersona(gruposPersonas) {
    const mainGroupByPersona = new Map();

    (gruposPersonas || []).forEach((gp) => {
        const pid = String(gp.persona_id);
        const gid =
            gp.grupo_id !== null && gp.grupo_id !== undefined
                ? String(gp.grupo_id)
                : null;
        if (!gid) return;

        const isPrincipal =
            gp.grupo_principal === true ||
            gp.grupo_principal === "true" ||
            gp.grupo_principal === 1;

        if (!mainGroupByPersona.has(pid)) {
            mainGroupByPersona.set(pid, {
                groupId: gid,
                principal: isPrincipal,
            });
        } else {
            const current = mainGroupByPersona.get(pid);
            if (!current.principal && isPrincipal) {
                mainGroupByPersona.set(pid, {
                    groupId: gid,
                    principal: true,
                });
            }
        }
    });

    return mainGroupByPersona;
}

// comment: group -> personas (only main group membership)
function computeGroupMembers(allIds, mainGroupByPersona) {
    const groupMembers = new Map();

    allIds.forEach((pid) => {
        const meta = mainGroupByPersona.get(pid);
        if (!meta) return;
        const gid = meta.groupId;
        if (!groupMembers.has(gid)) groupMembers.set(gid, []);
        groupMembers.get(gid).push(pid);
    });

    return groupMembers;
}

// comment: adjacency list from relaciones
function buildAdjacency(relaciones) {
    const adjacency = new Map();

    (relaciones || []).forEach((r) => {
        const a = String(r.persona_a);
        const b = String(r.persona_b);
        if (!a || !b || a === b) return;

        const relA = adjacency.get(a) || [];
        relA.push({ neighbor: b, rel: r });
        adjacency.set(a, relA);

        const relB = adjacency.get(b) || [];
        relB.push({ neighbor: a, rel: r });
        adjacency.set(b, relB);
    });

    return adjacency;
}

// comment: compute anchor persona for each ungrouped persona
function computeAnchorsForUngrouped(
    ungroupedIds,
    ungroupedSet,
    mainGroupByPersona,
    adjacency
) {
    const anchorForUngrouped = new Map();

    ungroupedIds.forEach((pid) => {
        const neighbors = adjacency.get(pid) || [];
        if (!neighbors.length) return;

        let chosen = null;

        // comment: prefer relacion_principal pointing to someone with group
        for (const { neighbor, rel } of neighbors) {
            if (!mainGroupByPersona.has(neighbor)) continue;
            if (
                rel.relacion_principal === true ||
                rel.relacion_principal === "true" ||
                rel.relacion_principal === 1
            ) {
                chosen = neighbor;
                break;
            }
        }

        // comment: fallback to any neighbor with group
        if (!chosen) {
            for (const { neighbor } of neighbors) {
                if (mainGroupByPersona.has(neighbor)) {
                    chosen = neighbor;
                    break;
                }
            }
        }

        if (chosen) {
            anchorForUngrouped.set(pid, chosen);
        }
    });

    // comment: propagate anchors through chains of ungrouped personas
    const queue = Array.from(anchorForUngrouped.keys());
    const visited = new Set(queue);

    while (queue.length > 0) {
        const current = queue.shift();
        const anchor = anchorForUngrouped.get(current);
        if (!anchor) continue;

        const neighbors = adjacency.get(current) || [];
        neighbors.forEach(({ neighbor }) => {
            if (!ungroupedSet.has(neighbor)) return;
            if (visited.has(neighbor)) return;
            anchorForUngrouped.set(neighbor, anchor);
            visited.add(neighbor);
            queue.push(neighbor);
        });
    }

    return anchorForUngrouped;
}

// comment: split ungrouped personas into anchored and free
function splitUngroupedPersons(
    ungroupedIds,
    anchorForUngrouped,
    mainGroupByPersona
) {
    const anchoredByGroup = new Map();
    const freeUngrouped = [];

    ungroupedIds.forEach((pid) => {
        const anchor = anchorForUngrouped.get(pid);
        if (!anchor) {
            freeUngrouped.push(pid);
            return;
        }
        const meta = mainGroupByPersona.get(anchor);
        if (!meta) {
            freeUngrouped.push(pid);
            return;
        }
        const gid = meta.groupId;
        if (!anchoredByGroup.has(gid)) anchoredByGroup.set(gid, []);
        anchoredByGroup.get(gid).push({ pid, anchor });
    });

    return { anchoredByGroup, freeUngrouped };
}

// comment: build logical clusters (groups + optional ungrouped cluster)
function buildClusters(
    list,
    grupos,
    groupMembers,
    anchoredByGroup,
    freeUngrouped
) {
    const clusters = [];

    // comment: keep group order stable using grupos when possible
    const groupOrder = (grupos || []).map((g) => String(g.grupo_id));
    const seenGroups = new Set();

    groupOrder.forEach((gid) => {
        const members = groupMembers.get(gid) || [];
        const extras = (anchoredByGroup.get(gid) || []).map((x) => x.pid);
        if (members.length === 0 && extras.length === 0) return;
        clusters.push({
            kind: "group",
            groupId: gid,
            members,
            extras,
            anchored: anchoredByGroup.get(gid) || [],
        });
        seenGroups.add(gid);
    });

    // comment: groups that exist in data but not in grupos array
    groupMembers.forEach((members, gid) => {
        if (seenGroups.has(gid)) return;
        const extras = (anchoredByGroup.get(gid) || []).map((x) => x.pid);
        clusters.push({
            kind: "group",
            groupId: gid,
            members,
            extras,
            anchored: anchoredByGroup.get(gid) || [],
        });
        seenGroups.add(gid);
    });

    // comment: one cluster for totally ungrouped personas
    if (freeUngrouped.length > 0) {
        clusters.push({
            kind: "ungrouped",
            groupId: null,
            members: [],
            extras: freeUngrouped.slice(),
            anchored: [],
        });
    }

    return clusters;
}

// comment: annotate clusters with ring radii
function computeClusterMeta(
    clusters,
    W,
    H,
    baseRadius,
    ringGap,
    computeRingRadius
) {
    const maxNodeSize = Math.max(W, H);

    return clusters.map((c) => {
        if (c.kind === "group") {
            const countMain = c.members.length;
            const countExtra = c.extras.length;
            const rMain = countMain > 0 ? computeRingRadius(countMain) : 0;
            const rExtra = countExtra > 0 ? rMain + ringGap : 0;
            const inner = rMain || rExtra || baseRadius;
            const outer = rExtra || rMain || baseRadius;
            return {
                ...c,
                rMain,
                rExtra,
                innerRadius: inner,
                outerRadius: outer + maxNodeSize * 0.6,
            };
        } else {
            const countExtra = c.extras.length;
            const r = computeRingRadius(countExtra);
            return {
                ...c,
                rMain: 0,
                rExtra: r,
                innerRadius: r,
                outerRadius: r + maxNodeSize * 0.6,
            };
        }
    });
}

// comment: cluster centers (one in global center, others around it)
function computeClusterCenters(
    clusterMeta,
    cx,
    cy,
    maxOuterRadius,
    clusterConfig
) {
    const clusterCount = clusterMeta.length;
    const centers = [];

    let clusterRingRadius = 0;
    if (clusterCount > 1) {
        const { minFactor, baseOffset, stepPerCluster } = clusterConfig;
        const factor = Math.max(
            minFactor,
            baseOffset + Math.max(0, clusterCount - 1) * stepPerCluster
        );
        clusterRingRadius = maxOuterRadius * factor;
    }

    clusterMeta.forEach((_, index) => {
        if (index === 0) {
            centers.push({ cx, cy });
            return;
        }

        if (clusterCount === 2 && index === 1) {
            centers.push({ cx: Math.floor(cx + clusterRingRadius), cy });
            return;
        }

        const angle =
            (2 * Math.PI * (index - 1)) / Math.max(1, clusterCount - 1);
        centers.push({
            cx: Math.floor(cx + clusterRingRadius * Math.cos(angle)),
            cy: Math.floor(cy + clusterRingRadius * Math.sin(angle)),
        });
    });

    return centers;
}

// comment: final node positions based on clusters and centers
function applyClusterLayoutToNodes(
    clusterMeta,
    clusterCenters,
    W,
    H,
    setNodes,
    setLayoutTick,
    getAvatarCenterOffset // NEW
) {
    const defaultOffset = { x: W / 2, y: H / 2 };

    const getOffset = (pid) => {
        if (typeof getAvatarCenterOffset === "function") {
            const c = getAvatarCenterOffset(pid);
            if (c && typeof c.x === "number" && typeof c.y === "number") {
                return c;
            }
        }
        return defaultOffset;
    };

    setNodes((prev) => {
        const byId = new Map();
        prev.forEach((nn) => {
            byId.set(nn.id, nn);
        });

        const next = new Map();

        const placeNodeAt = (pid, node, centerX, centerY) => {
            if (!node) return;
            const offset = getOffset(pid);
            const x = Math.floor(centerX - offset.x);
            const y = Math.floor(centerY - offset.y);
            next.set(pid, {
                ...node,
                position: { x, y },
            });
        };

        clusterMeta.forEach((cluster, idx) => {
            const center = clusterCenters[idx];
            const ccx = center.cx;
            const ccy = center.cy;

            const nMain = cluster.members.length;
            const angleMainByPersona = new Map();

            // main ring
            if (nMain > 0 && cluster.rMain > 0) {
                const step = (2 * Math.PI) / nMain;
                cluster.members.forEach((pid, i) => {
                    const angle = step * i;
                    angleMainByPersona.set(pid, angle);
                    const node = byId.get(pid);
                    const centerX = ccx + cluster.rMain * Math.cos(angle);
                    const centerY = ccy + cluster.rMain * Math.sin(angle);
                    placeNodeAt(pid, node, centerX, centerY);
                });
            }

            // extras ring
            if (cluster.rExtra > 0 && cluster.extras.length > 0) {
                if (cluster.kind === "group") {
                    const anchoredByAnchor = new Map();
                    cluster.anchored.forEach(({ pid, anchor }) => {
                        if (!anchoredByAnchor.has(anchor)) {
                            anchoredByAnchor.set(anchor, []);
                        }
                        anchoredByAnchor.get(anchor).push(pid);
                    });

                    anchoredByAnchor.forEach((pids, anchorId) => {
                        const baseAngle = angleMainByPersona.get(anchorId) || 0;

                        const minStepAngle = (() => {
                            const chord = W + NODE_GAP;
                            const ratio =
                                chord / (2 * Math.max(1, cluster.rExtra));
                            if (ratio >= 1) return Math.PI;
                            return 2 * Math.asin(ratio);
                        })();

                        if (pids.length === 1) {
                            const pid = pids[0];
                            const node = byId.get(pid);
                            const centerX =
                                ccx + cluster.rExtra * Math.cos(baseAngle);
                            const centerY =
                                ccy + cluster.rExtra * Math.sin(baseAngle);
                            placeNodeAt(pid, node, centerX, centerY);
                        } else {
                            const spread = minStepAngle * (pids.length - 1);
                            const start = baseAngle - spread / 2;

                            pids.forEach((pid, i) => {
                                const angle = start + minStepAngle * i;
                                const node = byId.get(pid);
                                const centerX =
                                    ccx + cluster.rExtra * Math.cos(angle);
                                const centerY =
                                    ccy + cluster.rExtra * Math.sin(angle);
                                placeNodeAt(pid, node, centerX, centerY);
                            });
                        }
                    });
                } else {
                    // ungrouped ring
                    const nExtra = cluster.extras.length;
                    const step = (2 * Math.PI) / nExtra;
                    cluster.extras.forEach((pid, i) => {
                        const angle = step * i;
                        const node = byId.get(pid);
                        const centerX = ccx + cluster.rExtra * Math.cos(angle);
                        const centerY = ccy + cluster.rExtra * Math.sin(angle);
                        placeNodeAt(pid, node, centerX, centerY);
                    });
                }
            }
        });

        prev.forEach((nn) => {
            if (next.has(nn.id)) return;
            next.set(nn.id, nn);
        });

        setLayoutTick((t) => t + 1);
        return Array.from(next.values());
    });
}

// comment: main layout orchestrator with clear configuration knobs
export function layoutPersonaNodes({
    personas,
    relaciones,
    gruposPersonas,
    grupos,
    setNodes,
    setLayoutTick,
    getAvatarCenterOffset,
}) {
    const list = personas || [];
    const total = list.length;
    if (total === 0) return;

    // comment: layout configuration (easy knobs)
    const W = NODE_SIZE_HINT.w; // comment: node width hint
    const H = NODE_SIZE_HINT.h; // comment: node height hint

    const nodeSpacing = W + NODE_GAP; // comment: desired spacing between nodes on a ring
    const ringBaseRadius = Math.max(W, H) * 1.2; // comment: base radius for first ring
    const ringGap = Math.max(W, H) + NODE_GAP; // comment: distance between rings (main vs extras)
    const extraRingPaddingFactor = 0.6; // comment: extra padding outside each cluster

    const clusterMinSeparationFactor = 1.5; // comment: minimal distance between cluster centers
    const clusterSeparationBaseOffset = 0.8; // comment: base offset for cluster separation factor
    const clusterSeparationStepPerCluster = 0.25; // comment: extra separation per additional cluster

    const cx = canvasCenter;
    const cy = canvasCenter;

    const baseRadius = ringBaseRadius;

    const computeRingRadius = (count) => {
        if (count <= 1) return baseRadius;
        const chord = nodeSpacing;
        const angle = Math.PI / Math.max(1, count);
        const needed = chord / (2 * Math.max(0.001, Math.sin(angle)));
        return Math.max(baseRadius, needed);
    };

    const allIds = list.map((p) => String(p?.persona_id ?? ""));

    const mainGroupByPersona = computeMainGroupByPersona(gruposPersonas);
    const groupMembers = computeGroupMembers(allIds, mainGroupByPersona);

    const ungroupedIds = allIds.filter((pid) => !mainGroupByPersona.has(pid));
    const ungroupedSet = new Set(ungroupedIds);

    const adjacency = buildAdjacency(relaciones);

    const anchorForUngrouped = computeAnchorsForUngrouped(
        ungroupedIds,
        ungroupedSet,
        mainGroupByPersona,
        adjacency
    );

    const { anchoredByGroup, freeUngrouped } = splitUngroupedPersons(
        ungroupedIds,
        anchorForUngrouped,
        mainGroupByPersona
    );

    const clusters = buildClusters(
        list,
        grupos,
        groupMembers,
        anchoredByGroup,
        freeUngrouped
    );

    const clusterMeta = computeClusterMeta(
        clusters,
        W,
        H,
        baseRadius,
        ringGap,
        computeRingRadius
    );

    const maxOuterRadius = clusterMeta.reduce(
        (acc, c) =>
            Math.max(
                acc,
                c.outerRadius + Math.max(W, H) * extraRingPaddingFactor
            ),
        baseRadius
    );

    const clusterConfig = {
        minFactor: clusterMinSeparationFactor,
        baseOffset: clusterSeparationBaseOffset,
        stepPerCluster: clusterSeparationStepPerCluster,
    };

    const clusterCenters = computeClusterCenters(
        clusterMeta,
        cx,
        cy,
        maxOuterRadius,
        clusterConfig
    );

    applyClusterLayoutToNodes(
        clusterMeta,
        clusterCenters,
        W,
        H,
        setNodes,
        setLayoutTick,
        getAvatarCenterOffset
    );
    return { clusterMeta, clusterCenters };
}

// comment: layout nodes in a roughly square grid (NxN) centered on canvas
export function layoutPersonaNodesGrid({
    setNodes,
    setLayoutTick,
    getAvatarCenterOffset,
}) {
    const CELL_WIDTH = 140;
    const CELL_HEIGHT = 120;

    const fallbackOffset = { x: AVATAR_SIZE / 2, y: AVATAR_SIZE / 2 };

    const getOffset = (pid) => {
        if (typeof getAvatarCenterOffset === "function") {
            const c = getAvatarCenterOffset(pid);
            if (c && typeof c.x === "number" && typeof c.y === "number") {
                return c;
            }
        }
        return fallbackOffset;
    };

    setNodes((prev) => {
        const total = prev.length;
        if (!total) {
            return prev;
        }

        const cols = Math.ceil(Math.sqrt(total));
        const rows = Math.ceil(total / cols);

        const gridWidth = (cols - 1) * CELL_WIDTH;
        const gridHeight = (rows - 1) * CELL_HEIGHT;

        const startX = canvasCenter - gridWidth / 2;
        const startY = canvasCenter - gridHeight / 2;

        const next = prev.map((node, index) => {
            const row = Math.floor(index / cols);
            const col = index % cols;

            const centerX = Math.floor(startX + col * CELL_WIDTH);
            const centerY = Math.floor(startY + row * CELL_HEIGHT);

            const offset = getOffset(node.id);
            const x = Math.floor(centerX - offset.x);
            const y = Math.floor(centerY - offset.y);

            return {
                ...node,
                position: { x, y },
            };
        });

        if (typeof setLayoutTick === "function") {
            setLayoutTick((t) => t + 1);
        }

        return next;
    });
}
